//* Libraries imports
import { motion } from 'framer-motion';

//? Styles imports
import './process-list-skeleton.scss';

interface ProcessListSkeletonProps {
  showSearch?: boolean;
  itemCount?: number;
}

export function ProcessListSkeleton({ showSearch = true, itemCount = 6 }: ProcessListSkeletonProps) {
  const skeletonVariants = {
    pulse: {
      opacity: [0.6, 1, 0.6],
      transition: {
        duration: 1.5,
        repeat: Infinity
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        duration: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  return (
    <motion.div
      className="process-list-skeleton w-100"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header Section Skeleton */}
      <motion.section 
        className="pt-4 p-2"
        variants={itemVariants}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div>
            {/* Title Skeleton */}
            <motion.div
              className="skeleton-page-title mb-2"
              variants={skeletonVariants}
              animate="pulse"
            />
            {/* Subtitle Skeleton */}
            <motion.div
              className="skeleton-page-subtitle"
              variants={skeletonVariants}
              animate="pulse"
            />
          </div>
          {/* Stats Badge Container Skeleton */}
          <motion.div
            className="d-flex align-items-center bg-light rounded px-3 py-2 shadow-sm"
            variants={skeletonVariants}
            animate="pulse"
          >
            <div className="d-flex align-items-center">
              <div className="skeleton-stats-label me-2" />
              <div className="skeleton-stats-badge" />
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Process List Section Skeleton */}
      <motion.section 
        className="pt-2"
        variants={itemVariants}
      >
        <div className="col-12">
          <div className="py-5 px-3">
            <div className="card-body" id="card">
              {/* Process Header Skeleton */}
              <motion.div
                className="skeleton-process-header mb-4"
                variants={skeletonVariants}
                animate="pulse"
              />

              {/* Search Section Skeleton */}
              {showSearch && (
                <div className="row">
                  <div className="col-12">
                    <div className="d-flex align-items-center mb-3">
                      <motion.div
                        className="skeleton-search-container flex-grow-1"
                        variants={skeletonVariants}
                        animate="pulse"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Table Section Skeleton */}
              <div className="row">
                <div className="col-12">
                  <div className="table-rep-plugin">
                    <div 
                      className="table-responsive mb-0 skeleton-table-container"
                      data-pattern="priority-columns"
                    >
                      <table className="table table-hover skeleton-table">
                        {/* Table Header Skeleton */}
                        <thead>
                          <tr>
                            <th className="skeleton-table-header">
                              <motion.div
                                className="skeleton-header-cell"
                                variants={skeletonVariants}
                                animate="pulse"
                              />
                            </th>
                            <th className="skeleton-table-header">
                              <motion.div
                                className="skeleton-header-cell"
                                variants={skeletonVariants}
                                animate="pulse"
                              />
                            </th>
                            <th className="skeleton-table-header">
                              <motion.div
                                className="skeleton-header-cell"
                                variants={skeletonVariants}
                                animate="pulse"
                              />
                            </th>
                            <th className="skeleton-table-header">
                              <motion.div
                                className="skeleton-header-cell"
                                variants={skeletonVariants}
                                animate="pulse"
                              />
                            </th>
                            <th className="skeleton-table-header">
                              <motion.div
                                className="skeleton-header-cell"
                                variants={skeletonVariants}
                                animate="pulse"
                              />
                            </th>
                            <th className="skeleton-table-header text-center" style={{ width: 150 }}>
                              <motion.div
                                className="skeleton-header-cell-actions"
                                variants={skeletonVariants}
                                animate="pulse"
                              />
                            </th>
                          </tr>
                        </thead>
                        
                        {/* Table Body Skeleton */}
                        <tbody>
                          {[...Array(itemCount)].map((_, index) => (
                            <motion.tr
                              key={index}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: index * 0.05, duration: 0.3 }}
                            >
                              {/* Process Name Column */}
                              <td>
                                <motion.div
                                  className="skeleton-table-cell-text"
                                  variants={skeletonVariants}
                                  animate="pulse"
                                  style={{ animationDelay: `${index * 0.1}s` }}
                                />
                              </td>
                              {/* Start Date Column */}
                              <td>
                                <motion.div
                                  className="skeleton-table-cell-date"
                                  variants={skeletonVariants}
                                  animate="pulse"
                                  style={{ animationDelay: `${index * 0.1}s` }}
                                />
                              </td>
                              {/* End Date Column */}
                              <td>
                                <motion.div
                                  className="skeleton-table-cell-date"
                                  variants={skeletonVariants}
                                  animate="pulse"
                                  style={{ animationDelay: `${index * 0.1}s` }}
                                />
                              </td>
                              {/* Online Column */}
                              <td>
                                <motion.div
                                  className="skeleton-table-cell-boolean"
                                  variants={skeletonVariants}
                                  animate="pulse"
                                  style={{ animationDelay: `${index * 0.1}s` }}
                                />
                              </td>
                              {/* Description Column */}
                              <td>
                                <motion.div
                                  className="skeleton-table-cell-description"
                                  variants={skeletonVariants}
                                  animate="pulse"
                                  style={{ animationDelay: `${index * 0.1}s` }}
                                />
                              </td>
                              {/* Actions Column */}
                              <td className="text-center">
                                <div className="d-flex gap-2 align-items-center justify-content-center">
                                  <motion.div
                                    className="skeleton-action-button"
                                    variants={skeletonVariants}
                                    animate="pulse"
                                    style={{ animationDelay: `${index * 0.1}s` }}
                                  />
                                </div>
                              </td>
                            </motion.tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Pagination Skeleton */}
                  <motion.div
                    className="skeleton-pagination-container mt-4"
                    variants={itemVariants}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      {/* Pagination Info Skeleton */}
                      <motion.div
                        className="skeleton-pagination-info"
                        variants={skeletonVariants}
                        animate="pulse"
                      />
                      
                      {/* Pagination Controls Skeleton */}
                      <div className="d-flex gap-2">
                        {[...Array(5)].map((_, index) => (
                          <motion.div
                            key={index}
                            className="skeleton-pagination-button"
                            variants={skeletonVariants}
                            animate="pulse"
                            style={{ animationDelay: `${index * 0.05}s` }}
                          />
                        ))}
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.section>
    </motion.div>
  );
}
